/**
 * Themakeuze (brandbook v2 p. 57): Systeem (standaard), Licht of Donker.
 * - <html data-theme="light|dark"> forceert; zonder attribuut volgt de pagina de systeemvoorkeur (brand.css).
 * - De keuze staat bij het account (profiles.ui_theme) en lokaal als cache, zodat er bij het laden geen flits is
 *   (public/theme-init.js leest dezelfde sleutel vóór de eerste weergave).
 */
export type ThemePref = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'antreja.theme';
/** Event op window na applyTheme, zodat componenten (bijv. het woordmerk) meewisselen. */
export const THEME_EVENT = 'antreja-theme';
export const THEME_LABEL: Readonly<Record<ThemePref, string>> = { system: 'Systeem', light: 'Licht', dark: 'Donker' };
export const THEME_HINT: Readonly<Record<ThemePref, string>> = {
  system: 'Volgt de instelling van je apparaat.',
  light: 'Altijd het lichte thema.',
  dark: 'Altijd het donkere thema, prettig bij weinig licht.',
};
/** Kleur van de browser-/statusbalk per thema (achtergrondtoken). */
export const THEME_BAR_COLOR: Readonly<Record<Theme, string>> = { light: '#003b4f', dark: '#071f28' };

export function isThemePref(v: unknown): v is ThemePref {
  return v === 'system' || v === 'light' || v === 'dark';
}

export function readStoredTheme(): ThemePref {
  try {
    const v = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
    return isThemePref(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

export function systemTheme(): Theme {
  try {
    return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function effectiveTheme(pref: ThemePref): Theme {
  return pref === 'system' ? systemTheme() : pref;
}

/** Zet het thema op <html> en bewaar het lokaal. */
export function applyTheme(pref: ThemePref): Theme {
  const root = globalThis.document?.documentElement;
  if (root) {
    if (pref === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', pref);
  }
  try {
    globalThis.localStorage?.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* lokaal opslaan is een gemak */
  }
  try {
    globalThis.dispatchEvent?.(new Event(THEME_EVENT));
  } catch {
    /* geen window (tests) */
  }
  return effectiveTheme(pref);
}

/** Luister naar een systeemwissel (alleen relevant bij 'system'). Geeft een opruimfunctie terug. */
export function onSystemThemeChange(cb: (t: Theme) => void): () => void {
  try {
    const mq = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return () => {};
    const h = (e: MediaQueryListEvent) => cb(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  } catch {
    return () => {};
  }
}
